import { Challenge, ChallengeMeta, User } from '../redux/prop-types';
import { checkpoints } from '../../../utils/nacional-utils';
import { AccessLevel } from './enums/access-levels';

// Funções Auxiliares de Verificação de Acesso

/**
 * Verifica se o usuário está bloqueado por expiração.
 */
export function isBlockedByExpiration(user: User): boolean {
  if (user.isBlockedByExpiration) {
    console.log('Acesso bloqueado por expiração.');
    return true;
  }
  return false;
}

/**
 * Verifica o acesso para usuários com nível BLOQUEADO.
 * Eles só podem acessar desafios já completados.
 */
export function isBlockedAccessAllowed(
  user: User,
  challengeId: string,
  completedChallengeIds: string[]
): boolean {
  if (user.access === AccessLevel.BLOCKED) {
    return completedChallengeIds.some(completed => completed === challengeId);
  }
  return false; // Não se aplica a este nível de acesso
}

/**
 * Verifica se o desafio é um dos primeiros liberados por padrão (Trilha 0 ou Trilha 1).
 */
export function isInitialChallengeAllowed(
  user: User,
  challenge: Challenge
): boolean {
  const { superOrder, phaseOrder, order, challengeOrder } = challenge;

  // Primeiro exercício da Trilha 0 para usuários do quiz 1
  if (
    user.registrationQuiz === 1 &&
    superOrder === 0 &&
    order === 0 &&
    challengeOrder === 0
  ) {
    console.log('Primeiro exercício trilha 0 - Liberado!');
    return true;
  }

  // Primeiro exercício da primeira trilha padrão para outros casos
  if (
    user.registrationQuiz !== 1 &&
    superOrder === 1 &&
    order === 3 &&
    phaseOrder === 0 &&
    challengeOrder === 0
  ) {
    console.log('Primeiro exercício trilha 1 - Liberado!');
    return true;
  }
  return false;
}

/**
 * Lógica de acesso para usuários com nível MODULAR.
 */
export function isModularAccessAllowed(
  user: User,
  challengeMeta: ChallengeMeta,
  challengeIndexForSuperBlock: number
): boolean | null {
  const userModule = user?.modules?.find(
    module => module.id === challengeMeta.superBlock
  );
  const moduleIsExpired = userModule?.status === 'expired';
  const hasLimitedAccess =
    user.access === AccessLevel.LIMITED || user.source === 'pagantes-modular';

  if (userModule && !moduleIsExpired) {
    return true;
  } else {
    if (hasLimitedAccess) {
      return challengeIndexForSuperBlock <= 10;
    }
    return false;
  }
}

/**
 * Verifica as restrições de currículo para acessos não modulares.
 */
export function isCurriculumAllowed(
  user: User,
  challengeMeta: ChallengeMeta
): boolean {
  if (
    user.classGroupInfo?.curriculumAvailable &&
    Array.isArray(user.classGroupInfo.curriculumAvailable)
  ) {
    if (
      user.registrationQuiz === 1 &&
      challengeMeta.superBlock === 'fundamentos-da-programacao-front-end'
    ) {
      return true;
    }
    const allowedCurriculums = user.classGroupInfo.curriculumAvailable;
    if (!allowedCurriculums.includes(challengeMeta.superBlock)) {
      console.log('Acesso negado: currículo não permitido.');
      return false;
    }
  }
  return true;
}

/**
 * Verifica restrições para usuários com acesso TRILHA1.
 */
export function isTrilha1AccessAllowed(
  user: User,
  challenge: Challenge
): boolean {
  // Usuários com acesso TRILHA1 não podem acessar superBlocks posteriores ao 1.
  if (user.access === AccessLevel.TRILHA1 && challenge.superOrder > 1) {
    console.log(
      'Acesso negado: Usuário Trilha1 tentando acessar superBlock > 1.'
    );
    return false;
  }
  return true;
}

/**
 * Verifica restrições para usuários LimitedNacional sem confirmação de matrícula.
 */
export function isLimitedNacionalAccessAllowed(
  user: User,
  challenge: Challenge,
  isUserRegistrationConfirmed?: boolean
): boolean {
  if (
    user.access === AccessLevel.LIMITED_NACIONAL &&
    !isUserRegistrationConfirmed &&
    challenge.superOrder >= checkpoints.firstCheckpoint.superOrder &&
    challenge.phaseOrder > checkpoints.firstCheckpoint.phaseOrder
  ) {
    console.log(
      'Acesso negado: Usuário LimitedNacional sem matrícula confirmada.'
    );
    return false;
  }
  return true;
}

/**
 * Verifica se o desafio anterior foi completado (regra geral para trilhas).
 */
export function isPreviousChallengeCompleted(
  challengeMeta: ChallengeMeta,
  completedChallengeIds: string[]
): boolean {
  return completedChallengeIds.some(
    completed => completed === challengeMeta.prevChallengeId
  );
}
